import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Spin,
  Select,
  InputNumber,
  Modal,
  DatePicker,
  Input,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { save } from "../../../../../../../features/save/saveSlice";
import { paymentStatusData } from "../../../../../../../data";
import { normalizeDateToLocalYear } from "../../../../../../../utils";
import { useWatch } from "antd/es/form/Form";

const PaymentAdd = ({ isModalOpen, handleCancel, handleFetch, prodd }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlSymbol = "api/v2/moneySymbol/get";
  const { activeObjOngoingProject, dataSymbol } = useSelector((state) => ({
    activeObjOngoingProject: state.obj.activeObj["ongoingProject"],
    dataSymbol: state.fetch.data[urlSymbol] || [],
  }));

  const onFinish = async (data) => {
    data.disbursementId = prodd?.disbursementId ? prodd?.disbursementId : null;
    data.pdProjectId = activeObjOngoingProject?.projectId;
    data.paidDate = normalizeDateToLocalYear(data?.paidDate);
    data.pdDueDate = normalizeDateToLocalYear(data?.pdDueDate);
    const res = await dispatch(
      save({
        ...data,
        url: "api/v2/mainProject/disbursement/save",
      }),
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await handleFetch();
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const onValuesChange = (changedValues, allValues) => {
    const { pdAmount, exchangeRate } = allValues;

    if (pdAmount && exchangeRate) {
      const converted = pdAmount * exchangeRate;
      form.setFieldsValue({ pdConvertedAmount: converted });
    }
  };

  useEffect(() => {
    if (prodd) {
      form.setFieldsValue({
        ...prodd,
        paidDate: prodd?.paidDate ? dayjs(new Date(prodd?.paidDate)) : null,
        pdDueDate: prodd?.pdDueDate ? dayjs(new Date(prodd?.pdDueDate)) : null,
      });
    } else {
      form.resetFields();
    }
  }, [prodd, form]);

  const pdStatus = useWatch("pdStatus", form);

  return (
    <Modal
      title={`${
        prodd?.disbursementId
          ? "Update Payment Record"
          : "Add a New Payment Record"
      }`}
      open={isModalOpen}
      onOk={onFinish}
      onCancel={handleCancel}
      className="!w-[59.5rem]"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          className=""
          label={"Project"}
          rules={[
            {
              required: false,
              message: "Please add project",
            },
          ]}
        >
          <Input
            disabled
            value={activeObjOngoingProject?.projectName}
            className="input"
          />
        </Form.Item>
        <Form.Item
          name="pdDescription"
          className=""
          label={"Payment Description"}
          rules={[
            {
              required: true,
              message: "Please add payment description",
            },
          ]}
        >
          <Input className="input" />
        </Form.Item>

        <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
          <Form.Item
            name="pdCurrencyId"
            className=""
            label={"Currency"}
            rules={[
              {
                required: true,
                message: "Please select currency",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={
                dataSymbol?.length > 0 &&
                dataSymbol?.map((item) => ({
                  value: item?.moneySybId,
                  label: item?.moneySybCode,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase(),
                )
              }
            />
          </Form.Item>

          <Form.Item
            name="pdAmount"
            label={"Amount"}
            rules={[
              {
                required: true,
                message: "Please add amount",
              },
            ]}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>
          <Form.Item
            name="exchangeRate"
            label={"Exchange Rate"}
            rules={[
              {
                required: true,
                message: "Please add Exchange Rate",
              },
              {
                validator: (_, value) =>
                  value > 0
                    ? Promise.resolve()
                    : Promise.reject("Conversion rate must be greater than 0"),
              },
            ]}
          >
            <InputNumber
              min={0.01}
              step={0.01}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="pdConvertedAmount"
            label={"Converted Amount"}
            rules={[
              {
                required: true,
                message: "Please add Converted Amount",
              },
            ]}
          >
            <InputNumber
              disabled
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="paymentAccount"
            className=""
            label={"Funding Type"}
            rules={[
              {
                required: false,
                message: "Please select funding type",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={[
                { value: "GRANT_LOAN", label: "Grant/Loan" },
                { value: "EXCHEQUER", label: "Exchequer" },
                { value: "FARMER_CONTRIBUTION", label: "Farmer Contribution" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="pdAdvancedRepaymentAmount"
            label={"Advance Repayment (Ksh)"}
            rules={[
              {
                required: false,
                message: "Please add advance repayment",
              },
              {
                validator: async (_, value) => {
                  const pdConvertedAmount =
                    form?.getFieldValue("pdConvertedAmount") ?? 0;
                  if (value > pdConvertedAmount) {
                    return Promise.reject(
                      "Advance payment cannot exceed total converted amount",
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="pdRetainedRepaymentAmount"
            label={"Retained Amount"}
            rules={[
              {
                required: false,
                message: "Please add Retained Amount",
              },
            ]}
          >
            <InputNumber
              min={0}
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              className="input flex"
            />
          </Form.Item>

          <Form.Item
            name="pdStatus"
            className=""
            label={"Status"}
            rules={[
              {
                required: true,
                message: "Please select status",
              },
            ]}
          >
            <Select
              className=""
              allowClear
              style={{
                width: "100%",
              }}
              options={
                paymentStatusData?.length > 0 &&
                paymentStatusData?.map((item) => ({
                  value: item?.value,
                  label: item?.label,
                }))
              }
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label?.toLocaleLowerCase() ?? "").includes(
                  input?.toLocaleLowerCase(),
                )
              }
            />
          </Form.Item>
          {pdStatus === "PENDING_PAYMENT" && (
            <Form.Item
              name="pdDueDate"
              rules={[
                {
                  required: false,
                  message: "Due date is required",
                },
              ]}
              label="Due Date"
            >
              <DatePicker format={"YYYY MMM DD"} className="input" />
            </Form.Item>
          )}

          {pdStatus === "PAID" && (
            <Form.Item
              name="paidDate"
              rules={[
                {
                  required: false,
                  message: "Payment date is required",
                },
              ]}
              label="Payment Date"
            >
              <DatePicker
                format={"YYYY MMM DD"}
                className="input"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
              />
            </Form.Item>
          )}
        </div>

        <div className="flex justify-between mt-[48px] ">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center mb-[58px] gap-x-5">
            <div className="w-[150px]">
              <button
                type="button"
                key="back"
                onClick={handleCancel}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[150px]">
              <button
                disabled={saving}
                key="submit"
                type="submit"
                className="cstm-btn-2"
              >
                {saving ? (
                  <Spin />
                ) : prodd?.disbursementId ? (
                  "Update Record"
                ) : (
                  "Save Record"
                )}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
export default PaymentAdd;
